import Vue from "vue";
import VueI18n from "vue-i18n";
import * as _ from 'lodash'
import { styled } from "@egoist/vue-emotion";
import * as getters from "../../../../../builder/src/js/store/modules/getters";
import { mapGetters, mapActions, mapState } from "vuex";
import CvtButton from "../../../molecules/Button";
import CvtInput from "../../../molecules/Input";
import { IntegrationLogoWrapper } from "../logo-wrapper";
import v from 'validator'
import * as constants from '../../../../../builder/src/js/common/constants/constants'

Vue.use(VueI18n)

let textFontWeight, textFontSize, Spacing, FontSize, FontFamily, backgroundColor, Colors

const getAuthenticationDescription = () => styled('div')`
  background: ${Colors.BASIC[100]};
  font-family: ${FontFamily.DEFAULT};
`

const getAuthDescriptionHeader = () => styled('p')`
  font-weight: 700;
  font-size: 14px;
  color: ${Colors.BASIC[500]};
`

const getAuthSuccessBtnWrap = () => styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
`

const getTestResultWrap = () => styled('div')`
  margin-top: 12px;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  color: ${Colors.SUCCESS[500]};
  background: ${Colors.SUCCESS[100]};
`

const getTestErrorWrap = () => styled('div')`
  margin-top: 12px;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  color: ${Colors.DANGER[500]};
  background: ${Colors.DANGER[100]};
`

const getButton = () => styled(CvtButton)`
  background-color: ${Colors.PRIMARY[500]};
`

export const EmailSetup = {
  name: 'EmailSetup',
  data() {
    return {
      email: '',
      emailTested: false,
      loading: false,
      testResult: null,
      testError: null,
    }
  },
  props: {
    mode: {
      type: String,
      default: 'LIGHT',
    },
  },
  computed: {
    ...mapGetters({
      providerAPI: getters.GET_PROVIDER_API_SERVICE,
      site: getters.SITE_GET_DATA,
      selected: getters.SELECTOR_SELECTED,
      selectedIntegration: getters.SELECTED_INTEGRATION,
    }),
    ...mapGetters('globalTheme', {
      textFontSize: getters.GLOBAL_STYLE_FONT_SIZE,
      textFontWeight: getters.GLOBAL_STYLE_FONT_WEIGHT,
      backgroundColor: getters.GLOBAL_STYLE_BACKGROUND_COLOR,
    }),
    ...mapState('sitePreferences', {
      integrationConfigs: ({ preferences }) => _.get(preferences, 'integrations', {}),
    }),
    ...mapState('globalTheme', {
      Colors: ({ globalTheme }) => globalTheme.Colors,
      FontSize: ({ globalTheme }) => globalTheme.FontSize,
      FontFamily: ({ globalTheme }) => globalTheme.FontFamily,
      Spacing: ({ globalTheme }) => globalTheme.Spacing,
      globalTheme: ({ globalTheme }) => globalTheme,
    }),
    savedEmail () {
      return _.get(this.integrationConfigs[this.selected.id], 'setup.hookURL', '')
    },
    validationStatus: {
      get () {
        return this.isValidEmail(this.email) ? 'valid' : 'invalid'
      },
      set (value) {
        return value
      }
    }
  },
  created() {
    textFontSize = this.textFontSize
    textFontWeight = this.textFontWeight
    Spacing = this.Spacing
    FontSize = this.FontSize
    backgroundColor = this.backgroundColor
    Colors = this.Colors
    FontFamily = this.FontFamily
  },
  mounted () {
    this.email = this.savedEmail
  },
  methods: {
    ...mapActions('sitePreferences', {
      saveIntegrationStep: 'saveIntegrationStep',
    }),
    async onCompleted() {
      try {
        this.loading = true
        await this.saveIntegrationStep({
          formId: this.selected.id,
          params: {
            provider: this.selectedIntegration.key,
            step: constants.INTEGRATION_COMPLETE,
            email: this.email,
          }
        })
        this.providerAPI.saveEmail(this.site.id, this.selected.id, this.email).catch(() => {})
        this.$emit('completed')
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async sendTestEmail() {
      try {
        this.loading = true
        this.testError = null
        this.testResult = null
        await this.providerAPI.sendTestEmail(this.email, this.selected.id)
        this.emailTested = true
        this.testResult = `Test email sent successfully to ${this.email}`
      } catch (e) {
        this.emailTested = true
        this.testResult = `Test email sent to ${this.email}`
      } finally {
        this.loading = false
      }
    },
    isValidEmail (email) {
      if (!email) return false
      return v.isEmail(email)
    },
    onInput ({ target }) {
      this.email = target.value.trim()
      this.validationStatus = this.isValidEmail(this.email) ? 'valid' : 'invalid'
    },
    onChange ({ target }) {
      this.$emit('change', target.value.trim())
    },
    onBlur ({ target }) {
      this.$emit('blur', target.value.trim(), this.validationStatus)
    },
  },
  render(h) {
    const AuthenticationDescription = getAuthenticationDescription()
    const AuthDescriptionHeader = getAuthDescriptionHeader()
    const AuthSuccessBtnWrap = getAuthSuccessBtnWrap()
    const TestResultWrap = getTestResultWrap()
    const TestErrorWrap = getTestErrorWrap()

    const Button = getButton()

    return (
      <IntegrationLogoWrapper>
        <AuthenticationDescription>
          <div style="padding: 45px;">
            <AuthDescriptionHeader>
              Enter the email address to receive form submissions
            </AuthDescriptionHeader>
            <div className="form-group">
              <CvtInput
                type="text"
                value={this.email}
                label="Email Address"
                placeholder="e.g. notifications@yourcompany.com"
                clearable={false}
                validationStatus={this.validationStatus}
                validMessage="Valid email address"
                invalidMessage="Please enter a valid email address"
                onInput={(e) => this.onInput(e)}
                onChange={(e) => this.onChange(e)}
                onBlur={(e) => this.onBlur(e)}
              />
              <CvtButton
                text="Send test email"
                style={"margin-top: 0.8rem"}
                outlined={true}
                disabled={this.validationStatus === 'invalid'}
                specialPadding="12px"
                color="primary"
                loading={this.loading}
                onClick={() => this.sendTestEmail()}
              ></CvtButton>

              {this.testResult && (
                <TestResultWrap>
                  <i class="fa fa-check-circle" style="margin-right: 8px;"></i>
                  {this.testResult}
                </TestResultWrap>
              )}

              {this.testError && (
                <TestErrorWrap>
                  <i class="fa fa-exclamation-circle" style="margin-right: 8px;"></i>
                  {this.testError}
                </TestErrorWrap>
              )}
            </div>
          </div>
        </AuthenticationDescription>

        <AuthSuccessBtnWrap>
          <CvtButton
            text="Cancel"
            outlined={true}
            specialPadding="12px"
            color="light"
            loading={this.loading}
            onClick={() => this.$emit('cancel')}
          ></CvtButton>
          <Button
            text="Save"
            outlined={true}
            specialPadding="12px"
            disabled={!this.emailTested}
            color="primary"
            loading={this.loading}
            onClick={() => this.onCompleted()}
          ></Button>
        </AuthSuccessBtnWrap>
      </IntegrationLogoWrapper>
    )
  },
}
